import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { street: "", city: "", state: "", zip: "", description: "" };

export function PropertyModal({ open, onOpenChange, property, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(property ? { ...EMPTY, ...property } : EMPTY);
  }, [open, property]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.street.trim()) { toast.error("Street address is required"); return; }
    setSaving(true);
    try {
      const body = { street: form.street, city: form.city, state: form.state, zip: form.zip, description: form.description };
      const { data } = property
        ? await api.put(`/service/properties/${property.id}`, body)
        : await api.post("/service/properties", body);
      toast.success(property ? "Address updated" : "Address added");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save address"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg" data-testid="property-modal">
        <DialogHeader>
          <DialogTitle>{property ? "Edit address" : "Add address"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-6">
            <Label>Street address *</Label>
            <Input value={form.street} onChange={set("street")} placeholder="635 E. Walnut" autoFocus data-testid="property-street-input" />
          </div>
          <div className="col-span-3">
            <Label>City</Label>
            <Input value={form.city} onChange={set("city")} data-testid="property-city-input" />
          </div>
          <div className="col-span-1">
            <Label>State</Label>
            <Input value={form.state} onChange={set("state")} maxLength={2} data-testid="property-state-input" />
          </div>
          <div className="col-span-2">
            <Label>ZIP</Label>
            <Input value={form.zip} onChange={set("zip")} data-testid="property-zip-input" />
          </div>
          <div className="col-span-6">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={set("description")} data-testid="property-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="property-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="property-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
